import styled from "styled-components";
import { IMPACT_ICONS, SIZES } from "@/components/constants";
import { useEffect, useRef, useState } from "react";
import { useGetDashBoardQuery } from "@/api/dashBoardApi";

const ImpactCountSection = ({ data }) => {
    const wrapRef = useRef(null);
    const startedRef = useRef(false);
    const { data: dashBoardData, isLading } = useGetDashBoardQuery();
    const [displayValues, setDisplayValues] = useState({});
    const items = !isLading && dashBoardData && dashBoardData.result ?
        [
            {
                key: "useCount",
                value: dashBoardData.data.usage_count,
                label: data["analysis-usage-count"],
                icon: IMPACT_ICONS.USE,
            },
            {
                key: "treeSaved",
                value: dashBoardData.data.saved_trees,
                label: data["analysis-saved-trees"],
                icon: IMPACT_ICONS.TREE,
            },
            {
                key: "carbonSaved",
                value: dashBoardData.data.carbon_reduction,
                label: data["analysis-carbon-reduction"],
                suffix: "kg",
                icon: IMPACT_ICONS.CARBON,
            },
        ]
        : [];

    useEffect(() => {
        if (!items.length) return;
        const el = wrapRef.current;
        if (!el) return;
        const startCountUp = () => {
            if (startedRef.current) return;
            startedRef.current = true;
            const init = {};
            items.forEach(item => {init[item.key] = 0});
            setDisplayValues(init);
            const duration = 2000;
            const startTime = performance.now();
            const updateCounter = (currentTime) => {
                const progress = Math.min((currentTime - startTime) / duration, 1);
                const next = {};
                items.forEach(item => {next[item.key] = Math.floor(progress * item.value)});
                setDisplayValues(next);
                if (progress < 1) {
                    requestAnimationFrame(updateCounter);
                }
            };
            requestAnimationFrame(updateCounter);
        };
        const io = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    startCountUp();
                }
            },
            { threshold: 0.25 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [dashBoardData]);

    return (
        <SectionWrap>
            <div className="container">
                <div className="title-area">
                    <h1 dangerouslySetInnerHTML={{__html: data["analysis-title"]}}/>
                    <p dangerouslySetInnerHTML={{__html: data["analysis-subtitle"]}}/>
                </div>

                <div className="impact-card-wrap" ref={wrapRef}>
                    {items.map((item, idx) => (
                        <div className={`impact-card ${idx !== 0 ? `item0${idx + 1}` : ""}`} key={idx}>
                            <div className="icon-wrap">{item.icon}</div>
                            <div className="count-num-wrap">
                                <span className="count-num">
                                    {Number(displayValues[item.key] || 0).toLocaleString()}
                                </span>
                                {
                                    item.suffix &&
                                    <span className="sub-text">
                                        {item.suffix}
                                    </span>
                                }
                            </div>
                            <p dangerouslySetInnerHTML={{__html: item.label}}/>
                        </div>
                    ))}
                </div>
                <div className="impact-desc">
                    <p dangerouslySetInnerHTML={{__html: data["analysis-desc"]}}/>
                </div>
            </div>
        </SectionWrap>
    );
};
const SectionWrap = styled.section`
    padding: 40px 0;
    background-color: #f7f7f7;
    .container {
        justify-content: center;
        align-items: center;
        flex-direction: column;
        display: flex;
        margin: 0 auto;
        padding: 0 20px;
        width: 100%;
        .title-area{
            margin-bottom: 48px;
            text-align: center;
            h1{
                color: #1f2937;
                letter-spacing: -0.025em;
                font-weight: 800;
                font-size: 30px;
                line-height: 36px;
                margin-bottom: 8px;
            }
            p{
                color: #6b7280;
                font-size: 18px;
                line-height: 28px;
            }
        }
        .impact-card-wrap{
            width: 100%;
            grid-template-columns: repeat(1, minmax(0, 1fr));
            display: grid;
            gap: 32px;
            .impact-card{
                padding: 24px;
                text-align: center;
                background-color: #fff;
                border-radius: 16px;
                border-bottom: 4px solid #10b981;
                transition: all 0.3s ease;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06);
                .icon-wrap{
                    display: flex;
                    justify-content: center;
                    margin-bottom: 16px;
                    svg{
                        color: #059669;
                    }
                }
                .count-num-wrap{
                    .count-num{
                        color: #111827;
                        font-weight: 700;
                        line-height: 1;
                        font-size: 60px;
                    }
                    .sub-text{
                        font-weight: 400;
                        font-size: 30px;
                        line-height: 36px;
                    }
                }
                & > p{
                    margin-top: 10px;
                    font-weight: 600;
                    font-size: 20px;
                    line-height: 28px;
                    color: #047857;
                }
                &.item02{
                    border-color: #84cc16;
                    .icon-wrap{
                        svg {
                            color: #65a30d;
                        }
                    }
                    & > p{
                        color: #4d7c0f;
                    }
                }
                &.item03{
                    border-color: #14b8a6;
                    .icon-wrap{
                        svg {
                            color: #0d9488;
                        }
                    }
                    & > p{
                        color: #0f766e;
                    }
                }

            }
        }
        .impact-desc{
            width: 100%;
            text-align: center;
            padding: 32px 0 0;
            margin-top: 48px;
            border-top: 1px solid #e5e7eb;
            p{
                color: #4b5563;
            }
        }
    }

    @media (min-width: ${SIZES.MOBILE}) {
        .container {
            .title-area{
                h1{
                    font-size: 36px;
                    line-height: 40px;
                }
            }
        }
    }
    @media (min-width: ${SIZES.MOBILE_L}) {
        .container {
            //max-width: 768px;
            max-width: 1152px;
            .impact-card-wrap{
                grid-template-columns: repeat(3, minmax(0, 1fr));
                gap: 32px;
                .impact-card{
                    &:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
                    }
                }
            }
        }
    }
    @media (min-width: ${SIZES.TABLET}) {
        padding: 120px 0;
        .container {
            //max-width: 1024px;
        }
    }
    @media (min-width: ${SIZES.TABLET_L}) {
        .container {
            
        }
    }
    @media (min-width: ${SIZES.PC}) {
        .container {
            //max-width: 1536px;
        }
    }
`
export default ImpactCountSection;